import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, MailOpen, Inbox, XCircle, Trash2 } from "lucide-react";
import { fmtDate } from "@/components/admin/AdminShared";

export const AdminMessagesTab = ({ messages, onOpenMessage }) => (
  <Card className="bg-af-card border-af rounded-md overflow-hidden" data-testid="admin-messages-section">
    {messages.length === 0 ? (
      <div className="p-12 text-center">
        <Inbox className="h-10 w-10 text-af-muted mx-auto mb-3" />
        <p className="text-af-muted text-sm">Aucun message pour le moment.</p>
      </div>
    ) : (
      <ul className="divide-y divide-af">
        {messages.map((m) => (
          <li
            key={m.id}
            className={
              "flex items-start gap-3 p-4 hover:bg-af-subtle cursor-pointer transition-colors " +
              (!m.read ? "bg-[#FBF5E2]" : "")
            }
            onClick={() => onOpenMessage(m)}
            data-testid={`admin-message-row-${m.id}`}
          >
            <div
              className={
                "h-9 w-9 rounded-full flex items-center justify-center shrink-0 " +
                (!m.read ? "bg-terracotta text-white" : "bg-af-subtle border border-af text-af-muted")
              }
            >
              {m.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={"text-sm truncate " + (!m.read ? "font-bold text-af" : "font-semibold text-af-secondary")}>
                  {m.name}
                </span>
                <span className="text-xs text-af-muted truncate">&lt;{m.email}&gt;</span>
                {!m.read && (
                  <span className="text-[10px] uppercase tracking-wider bg-terracotta text-white px-1.5 py-0.5 rounded-sm font-bold">
                    Nouveau
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-af truncate">{m.subject || "(sans objet)"}</div>
              <div className="text-xs text-af-muted truncate mt-0.5">{m.message}</div>
            </div>
            <div className="text-xs text-af-muted whitespace-nowrap pt-1">{fmtDate(m.created_at)}</div>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export const MessageDialog = ({ message, onClose, onToggleRead, onDelete }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <Card
      className="bg-af-card border-af rounded-md p-7 max-w-xl w-full max-h-[85vh] overflow-y-auto"
      data-testid="admin-message-dialog"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-1">
            Message reçu — {fmtDate(message.created_at)}
          </div>
          <h3 className="font-display font-extrabold text-2xl mb-1">{message.subject || "(sans objet)"}</h3>
          <div className="text-sm text-af-secondary">
            De <strong className="text-af">{message.name}</strong> &lt;
            <a href={`mailto:${message.email}`} className="text-terracotta hover:underline">{message.email}</a>
            &gt;
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="admin-message-close"
          className="text-af-muted hover:text-af p-1"
          aria-label="Fermer"
        >
          <XCircle className="h-5 w-5" />
        </button>
      </div>
      <div className="bg-af-subtle border border-af rounded-md p-4 text-sm text-af leading-relaxed whitespace-pre-wrap mb-5">
        {message.message}
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <Button
          variant="outline"
          onClick={() => onToggleRead(message, !message.read)}
          data-testid="admin-message-toggle-read"
          className="border-af text-af-secondary hover:bg-af-subtle"
        >
          {message.read ? (
            <><Mail className="h-4 w-4 mr-2" /> Marquer comme non lu</>
          ) : (
            <><MailOpen className="h-4 w-4 mr-2" /> Marquer comme lu</>
          )}
        </Button>
        <a href={`mailto:${message.email}?subject=Re: ${encodeURIComponent(message.subject || '')}`}>
          <Button className="bg-terracotta hover:bg-[#9C7124] text-white" data-testid="admin-message-reply">
            <Mail className="h-4 w-4 mr-2" /> Répondre
          </Button>
        </a>
        <Button
          variant="outline"
          onClick={() => onDelete(message)}
          data-testid="admin-message-delete"
          className="border-[#9F1239] text-[#9F1239] hover:bg-[#FDECEC]"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer
        </Button>
      </div>
    </Card>
  </div>
);
